/**
 * Time Related Tools for FlowRide Frontend
 * Provides utilities for time formatting, conversion, and manipulation.
 * Handles various time formats and duration calculations.
 */

/**
 * Format seconds to human-readable duration string
 * Based on formatSecondsVerbose, with options for compact format and seconds display
 * @param seconds - Duration in seconds
 * @param includeSeconds - Whether to include seconds in the output (default: true)
 * @param compact - Whether to use compact format (hr/mn/s) or verbose format (heure/minute/secondes) (default: false)
 * @returns Formatted duration string (e.g., "1 heure 30 minutes 45 secondes" or "1hr 30mn 45s")
 */
export function formatTime( seconds: number, compact: boolean = false, includeSeconds: boolean = false ): string {
    const total = Math.max( 0, Math.floor( seconds ) );
    const hours = Math.floor( total / 3600 );
    const minutes = Math.floor( ( total % 3600 ) / 60 );
    const secs = total % 60;
    const parts: string[] = [];

    if ( hours > 0 ) {
        parts.push( `${ hours }${ compact ? "hr" : ` heure${ hours > 1 ? "s" : "" }` }` );
    }
    if ( minutes > 0 ) {
        parts.push( `${ minutes }${ compact ? "mn" : ` minute${ minutes > 1 ? "s" : "" }` }` );
    }

    // Show seconds based on format and includeSeconds parameter
    if ( includeSeconds || ( hours === 0 && minutes === 0 ) ) {
        if ( compact ) {
            // Compact format: always show seconds if > 0 or total === 0
            if ( secs > 0 || total === 0 ) {
                parts.push( `${ secs }s` );
            }
        } else {
            // Verbose format: only show seconds if no hours/minutes
            if ( ( secs > 0 || total === 0 ) && minutes === 0 && hours === 0 ) {
                parts.push( `${ secs } secondes` );
            }
        }
    }

    return parts.join( " " ).trim();
}

/**
 * Convert seconds to minutes with no decimal
 * @param seconds - Number of seconds
 * @returns Number of minutes (floored)
 */
export function secondsToMinutes( seconds: number ): number {
    return Math.round( seconds / 60 );
}

/**
 * Get date string in YYYY-MM-DD format
 * @param dateString - Date string to convert
 * @returns Date string in ISO format
 */
export function getDateString( dateString: string ): string {
    const date = new Date( dateString );

    return date.toISOString().slice( 0, 10 );
}

/**
 * Convert date to time string in HH:MM format
 * @param date - Date string or number
 * @returns Time string in HH:MM format
 */
export function timeToString( date: string | number ): string {
    const TimeStamp = new Date( date );
    const hours = TimeStamp.getHours().toString().
        padStart( 2, "0" );
    const minutes = TimeStamp.getMinutes().toString().
        padStart( 2, "0" );

    return `${ hours }:${ minutes }`;
}

/**
 * Format date number to readable date string
 * @param dateNumber - Date timestamp
 * @returns Formatted date string (e.g., "15.03.2024 - 14:30")
 */
export function formatDate( dateNumber: number ) {
    const date = new Date( dateNumber );
    const year = date.getFullYear();
    const month = ( date.getMonth() + 1 ).toString().padStart( 2, "0" );
    const day = date.getDate().toString().
        padStart( 2, "0" );
    const hours = date.getHours().toString().
        padStart( 2, "0" );
    const minutes = date.getMinutes().toString().
        padStart( 2, "0" );

    return `${ day }.${ month }.${ year } - ${ hours }:${ minutes }`;
}

/**
 * Format timestamp for HTML input datetime-local
 * @param timestamp - Date timestamp
 * @returns Formatted date string for input (e.g., "2024-03-15T14:30")
 */
export function formatDateForInput( timestamp: number ) {
    const date = new Date( timestamp );
    const year = date.getFullYear();
    const month = String( date.getMonth() + 1 ).padStart( 2, "0" ); // Month from 0-11 to 1-12
    const day = String( date.getDate() ).padStart( 2, "0" );
    const hours = String( date.getHours() ).padStart( 2, "0" );
    const minutes = String( date.getMinutes() ).padStart( 2, "0" );

    return `${ year }-${ month }-${ day }T${ hours }:${ minutes }`;
}

